import React from 'react';

interface RegionComparisonTableProps {
  data: Array<{
    roID?: number;
    roName: string;
    totalSchools: number;
    pmshriCount: number;
    nonPmshriCount: number;
    pmshriPercentage: string | number;
    districts?: number;
  }>;
  title: string;
  onRowClick?: (region: any) => void;
}

const RegionComparisonTable: React.FC<RegionComparisonTableProps> = ({
  data,
  title,
  onRowClick,
}) => {
  // Sort by total schools descending
  const sortedData = [...data].sort((a, b) => b.totalSchools - a.totalSchools);

  // Calculate totals
  const totals = sortedData.reduce(
    (acc, item) => ({
      totalSchools: acc.totalSchools + item.totalSchools,
      pmshriCount: acc.pmshriCount + item.pmshriCount,
      nonPmshriCount: acc.nonPmshriCount + item.nonPmshriCount,
      districts: acc.districts + (item.districts || 0),
    }),
    { totalSchools: 0, pmshriCount: 0, nonPmshriCount: 0, districts: 0 }
  );

  const overallPmshriPct =
    totals.totalSchools > 0
      ? ((totals.pmshriCount / totals.totalSchools) * 100).toFixed(1)
      : '0';

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
        <p className="text-gray-400">No data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                S.No
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Region
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Total Schools
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-green-600 uppercase tracking-wider">
                PM-SHRI
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-blue-600 uppercase tracking-wider">
                Non PM-SHRI
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-purple-600 uppercase tracking-wider">
                PM-SHRI %
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Progress
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {sortedData.map((item, index) => {
              const pct = parseFloat(String(item.pmshriPercentage)) || 0;
              return (
                <tr
                  key={item.roID || index}
                  className={`hover:bg-gray-50 transition-colors ${
                    onRowClick ? 'cursor-pointer' : ''
                  }`}
                  onClick={() => onRowClick?.(item)}
                >
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {index + 1}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">
                    {item.roName}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-700">
                    {item.totalSchools.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-green-600 font-medium">
                    {item.pmshriCount.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-blue-600 font-medium">
                    {item.nonPmshriCount.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-purple-600 font-semibold">
                    {pct.toFixed(1)}%
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-gradient-to-r from-green-400 to-green-600 h-2.5 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      ></div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-purple-50 font-semibold">
              <td className="px-4 py-3 text-sm text-gray-700" colSpan={2}>
                Total
              </td>
              <td className="px-4 py-3 text-sm text-center text-gray-800">
                {totals.totalSchools.toLocaleString()}
              </td>
              <td className="px-4 py-3 text-sm text-center text-green-700">
                {totals.pmshriCount.toLocaleString()}
              </td>
              <td className="px-4 py-3 text-sm text-center text-blue-700">
                {totals.nonPmshriCount.toLocaleString()}
              </td>
              <td className="px-4 py-3 text-sm text-center text-purple-700">
                {overallPmshriPct}%
              </td>
              <td className="px-4 py-3 text-sm">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-gradient-to-r from-purple-400 to-purple-600 h-2.5 rounded-full"
                    style={{ width: `${Math.min(parseFloat(overallPmshriPct), 100)}%` }}
                  ></div>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default RegionComparisonTable;
